import TR from 'utils/TR';
import T from 'utils/T';
import ex from 'utils/ex';
import CaptionBox from 'components/common/CaptionBox';
import React from 'react';
import GlocoseAbnormalsChart from 'components/charts/GlocoseAbnormalsChart';
import SummaryBloodGlocoseCountBox from 'components/common/SummaryBloodGlocoseCountBox';
import AbnormalBloodGlocoseBoxChart from 'components/charts/AbnormalBloodGlocoseBoxChart';
import HBox from 'components/common/HBox';
import VBox from 'components/common/VBox';
import Ratiobar from 'components/charts/Ratiobar';
import useSetChartData from 'hooks/app/useSetChartData';
import html2canvas from 'html2canvas';
import useRect from 'hooks/common/useRect';
import Section from 'components/common/Section';

const codes = [
  '새벽',
  '오전',
  '오후',
  '저녁'
];
export default function GlocoseAbnormals(props) {
  const {
    data,
    duration,
    ...rest
  } = props || {};
  const {
    abnormal_bld_sugr_time_list = [],
    abnormal_bld_sugr_graph_list,
  } = data || {};

  const hash = React.useMemo(() => abnormal_bld_sugr_time_list.reduce((a, c) => {
    return ({
      ...a,
      [codes[+c.time_cd - 1]]: {
        high: c.high_bld_sugr_cnt,
        low: c.low_bld_sugr_cnt
      },
      Total: {
        high: a.Total.high + c.high_bld_sugr_cnt,
        low: a.Total.low + c.low_bld_sugr_cnt,
        count: a.Total.count + c.low_bld_sugr_cnt + c.high_bld_sugr_cnt,
      }
    });
  }, {
    Total: { high: 0, low: 0, count: 0 },
  }), [abnormal_bld_sugr_time_list]);

  const ref = React.useRef();
  const rect = useRect(ref);
  const setChartBarData = useSetChartData('GlocoseDistributionBarChart');
  const setChartScatterData = useSetChartData('GlocoseDistributionScatterChart');

  const chartRef = React.useRef();
  const barChart = React.useMemo(() => (
    <HBox
      between
      {...ex('GlocoseAbnormalCount')}
      {...{ ref: chartRef }}
    >
      <VBox>
        <VBox full>
          <Ratiobar
            {...{
              duration,
              limits: [hash.Total.high, hash.Total.low],
              values: [hash.Total.high, hash.Total.low],
              colors: ['#FF995A', '#5D3FC5'],
              maxHeight: 90,
            }}
          />
        </VBox>
        <HBox center>
          <CaptionBox {...ex('mini', { caption: T('전체') })} />
        </HBox>
      </VBox>
      <VBox>
        <VBox full>
          <Ratiobar
            {...{
              duration,
              limits: [hash.Total.high, hash.Total.low],
              values: [hash['새벽']?.high || 0, hash['새벽']?.low || 0],
              colors: ['#FF995A', '#5D3FC5'],
              maxHeight: 90,
            }}
          />
        </VBox>
        <HBox center>
          <CaptionBox {...ex('mini', { caption: T('새벽') })} />
        </HBox>
      </VBox>
      <VBox>
        <VBox full>
          <Ratiobar
            {...{
              duration,
              limits: [hash.Total.high, hash.Total.low],
              values: [hash['오전']?.high || 0, hash['오전']?.low || 0],
              colors: ['#FF995A', '#5D3FC5'],
              maxHeight: 90,
            }}
          />
        </VBox>
        <HBox center>
          <CaptionBox {...ex('mini', { caption: T('오전') })} />
        </HBox>
      </VBox>
      <VBox>
        <VBox full>
          <Ratiobar
            {...{
              duration,
              limits: [hash.Total.high, hash.Total.low],
              values: [hash['오후']?.high || 0, hash['오후']?.low || 0],
              colors: ['#FF995A', '#5D3FC5'],
              maxHeight: 90,
            }}
          />
        </VBox>
        <HBox center>
          <CaptionBox {...ex('mini', { caption: T('오후') })} />
        </HBox>
      </VBox>
      <VBox>
        <VBox full>
          <Ratiobar
            {...{
              duration,
              limits: [hash.Total.high, hash.Total.low],
              values: [hash['저녁']?.high || 0, hash['저녁']?.low || 0],
              colors: ['#FF995A', '#5D3FC5'],
              maxHeight: 90,
            }}
          />
        </VBox>
        <HBox center>
          <CaptionBox {...ex('mini', { caption: T('저녁') })} />
        </HBox>
      </VBox>
    </HBox>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [duration, hash, rect]);
  const completeTimeoutIdRef = React.useRef();
  React.useEffect(() => {
    if (data.uninitialized) return;
    if (duration) return;
    if (!chartRef.current) return;
    clearTimeout(completeTimeoutIdRef.current);
    completeTimeoutIdRef.current = setTimeout(() => {
      clearTimeout(completeTimeoutIdRef.current);
      const run = async () => {
        if (!chartRef.current) return;
        const {
          width,
          height,
        } = chartRef.current.getBoundingClientRect();
        if (!width) return;
        if (!height) return;
        const canvas = await html2canvas(chartRef.current, {
          scale: 2,
          logging: false,
        });
        const url = canvas.toDataURL('image/png');
        setChartBarData((data) => ({ url, width, height }));
        URL.revokeObjectURL(url);
        canvas.remove();
      };
      run();
    }, 6000);
  }, [setChartBarData, barChart, duration, data.uninitialized]);

  const chartDataTimeoutRef = React.useRef();
  const onComplete = React.useCallback((animation) => {
    if (data.uninitialized) return;
    if (duration) return;
    if (!animation.chart.canvas.width) return;
    if (!animation.chart.canvas.height) return;
    clearTimeout(chartDataTimeoutRef.current);
    const url = animation.chart.canvas.toDataURL('image/png');
    const width = animation.chart.canvas.width;
    const height = animation.chart.canvas.height;
    chartDataTimeoutRef.current = setTimeout(() => {
      setChartScatterData && setChartScatterData(() => ({ url, width, height }));
      URL.revokeObjectURL(url);
    }, 6000);
  }, [data.uninitialized, duration, setChartScatterData]);

  return (
    <Section
      {...ex('GlocoseAbnormals', rest)}
      {...{
        ref,
        caption: T('이상혈당 분포'),
      }}
    >
      <HBox full>
        {barChart}
        <HBox full>
          <AbnormalBloodGlocoseBoxChart key={data.uninitialized} {...{ list: abnormal_bld_sugr_graph_list, duration, onComplete }} />
        </HBox>
      </HBox>
    </Section>
  );
}
